import { useBreakPoint, useModal } from "@hooks";
import { ModalPage, ModalPageBottom } from "@organisms";
import { useTranslation } from "react-i18next";
import { Group, actions, useAppDispatch, withAsyncThunkErrorHandling } from "@lib/store";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { H4 } from "@quarks";
import { GROUP_AVATAR_DEFINITION, GroupAvatarDefinition } from "@lib/logic";
import { GroupAvatar, Input } from "@molecules";
import { GroupMemberSelectionModal } from "../GroupMemberSelectionModal";

export const GroupInformationSelectionModal = ({ group }: { group?: Group }) => {
  const { t } = useTranslation();
  const { closeModal, addPages } = useModal();
  const dispatch = useAppDispatch();

  const [groupTitle, setGroupTitle] = useState(group?.name || "");
  const [selectedAvatar, setSelectedAvatar] = useState<GroupAvatarDefinition | null>(
    GROUP_AVATAR_DEFINITION.find(avatar => avatar.color === group?.color && avatar.emoji === group?.emoji) || null,
  );

  const isEditMode = !!group;

  const selectionValid = useMemo(() => {
    return groupTitle.trim().length > 0 && selectedAvatar !== null;
  }, [groupTitle, selectedAvatar]);

  const handleSave = useCallback(async () => {
    if (!selectionValid || !isEditMode || !group) return;

    const currentMemberIds = group.members.map(m => m.user.id);

    const result = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.updateGroup({
          groupId: group.id,
          dto: {
            name: groupTitle,
            memberUserIds: currentMemberIds,
            imageUrl: group.imageUrl,
            emoji: selectedAvatar?.emoji || "",
            color: selectedAvatar?.color || "",
          },
        }),
      ),
    );

    if (result.success) {
      closeModal();
      toast.success(t("groups:GroupUpdatedToast"));
    }
  }, [
    selectionValid,
    isEditMode,
    group,
    dispatch,
    groupTitle,
    selectedAvatar?.emoji,
    selectedAvatar?.color,
    closeModal,
    t,
  ]);

  const handleNext = () => {
    // Handle next logic here
    addPages([
      <GroupMemberSelectionModal
        groupAvatar={selectedAvatar}
        groupTitle={groupTitle}
      />,
    ]);
  };
  const breakpoint = useBreakPoint();

  const handleClick = isEditMode ? handleSave : handleNext;
  return (
    <ModalPage
      bottom={
        <ModalPageBottom
          buttonLabel={isEditMode ? t("translation:Save") : t("translation:Next")}
          cancelButtonLabel={t("translation:Cancel")}
          onCancel={closeModal}
          onClick={handleClick}
          buttonDisabled={!selectionValid}
        />
      }
      onClose={closeModal}
      title={isEditMode ? t("groups:EditLabel") : t("groups:CreateLabel")}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <H4>{t("groups:NameInputLabel")}</H4>

          <Input
            type="text"
            size="default"
            value={groupTitle}
            onChange={e => setGroupTitle(e.target.value)}
            className="border rounded-lg py-2 w-full focus-within:border-mapiq-black-800* hover:border-mapiq-black-800*"
            placeholder={t("groups:NameInputPlaceholder")}
            maxLength={40}
          />
        </div>

        <div className="flex flex-col gap-4">
          <H4>{t("groups:AvatarLabel")}</H4>

          <div className="grid grid-cols-[repeat(5,minmax(40px,72px))] gap-x-3 gap-y-2 md:gap-x-5 md:gap-y-4  justify-between ">
            {GROUP_AVATAR_DEFINITION.map((avatar, index) => {
              const isSelected = selectedAvatar?.emoji === avatar.emoji && selectedAvatar?.color === avatar.color;
              return (
                <button
                  key={`avatar-${index}-${avatar.emoji}-${avatar.color}`}
                  className={`aspect-square size-full rounded-lg bg-gray-100 flex items-center justify-center transition-all duration-200 ${
                    isSelected ? "border-2 border-gray-800" : "border border-transparent hover:bg-gray-200"
                  }`}
                  onClick={() => setSelectedAvatar(avatar)}>
                  <GroupAvatar
                    className="shrink-0"
                    size={breakpoint === "small" || breakpoint === "medium" ? "medium" : "large"}
                    group={{
                      id: "temp-id",
                      imageUrl: null,
                      ...avatar,
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </ModalPage>
  );
};
